import styled from 'styled-components';

import { Card } from '@components/card';
import { HorizontalTabs } from '@components/horizontal-tabs';
import { IconButton } from '@components/icon-button';
import { IconShadowContainer } from '@components/icon-shadow-container';
import { IconCloseRegular } from '@components/icons/close-regular';
import {
  HEADER_FOOTER_HEIGHT,
  hideModal,
  Modal,
  MODAL_MAX_HEIGHT,
  useModal,
} from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AIModel } from 'api/types';

import {
  getModelHelpText,
  getModelLabel,
  getModelProviderName,
  getVisibleModels,
  ModelDefinition,
  locale as modelLocale,
  ModelProvider,
  PROVIDER_ICONS,
  PROVIDER_ORDER,
} from './ai-agent-config';

type ModelSelectorModalProps = {
  currentModel: AIModel;
  hiddenModels: string[];
  onSelectModel(model: AIModel): void;
};

const getProviderModels = (provider: ModelProvider, models: ModelDefinition[]) => {
  return models.filter((modelDef) => {
    return modelDef.provider === provider;
  });
};

export const useModelSelectorModal = () =>
  useModal(ModelSelectorModal, { variant: 'LARGE', backgroundColor: 'gradient' });

const ModelSelectorModal = ({
  currentModel,
  hiddenModels,
  onSelectModel,
}: ModelSelectorModalProps) => {
  const { t } = useScopedTranslate(locale);
  const visibleModels = getVisibleModels(hiddenModels);
  const currentProvider = getModelProviderName(currentModel);
  const defaultTabIndex = Math.max(PROVIDER_ORDER.indexOf(currentProvider), 0);

  const tabItems = PROVIDER_ORDER.map((provider) => {
    const Icon = PROVIDER_ICONS[provider];
    return {
      label: provider,
      content: (
        <TabPanelContent>
          <Stack space={6}>
            <ProviderOverviewCard>
              <IconShadowContainer
                icon={<Icon size={52} color="black100" />}
                containerSize={84}
                iconSize={52}
                shadowSize={180}
                style={{ backgroundColor: theme.color.white100 }}
              />
              <Text font="bold16" text={provider} />
            </ProviderOverviewCard>
            <Stack space={2}>
              {getProviderModels(provider, visibleModels).map((modelDef) => (
                <Card
                  key={modelDef.value}
                  variant="transparent"
                  interactive
                  space={3}
                  onClick={() => {
                    onSelectModel(modelDef.value);
                    hideModal();
                  }}
                  style={{ cursor: 'pointer', border: 'none' }}
                >
                  <Text font="semibold14" text={getModelLabel(modelDef.value)} />
                  <Text
                    color="textSecondary"
                    font="regular12"
                    text={getModelHelpText(modelDef.value)}
                  />
                </Card>
              ))}
            </Stack>
          </Stack>
        </TabPanelContent>
      ),
    };
  });

  return (
    <Modal>
      <Modal.Header hasDivider={false}>
        <ModalHeaderRow>
          <Modal.HeaderTitle title={t('aiModels')} />
          <IconButton variant="ghost-purple" icon={<IconCloseRegular />} onClick={hideModal} />
        </ModalHeaderRow>
      </Modal.Header>
      <HorizontalTabs items={tabItems} defaultIndex={defaultTabIndex} />
    </Modal>
  );
};

const ModalHeaderRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const MAX_TAB_CONTENT_HEIGHT = 664;

const TabPanelContent = styled(Modal.Content)({
  // Held against the modal's own cap, minus the header and the tab bar above it.
  // Derived rather than measured, so a change to the header cannot leave it stale.
  height: `min(${pxToRem(
    MAX_TAB_CONTENT_HEIGHT
  )}, calc(${MODAL_MAX_HEIGHT} - ${HEADER_FOOTER_HEIGHT} - ${theme.base.height}))`,
  overflowY: 'auto',
  padding: `${pxToRem(24)} ${pxToRem(40)} ${pxToRem(8)}`,
});

const ProviderOverviewCard = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 4,
})({
  padding: pxToRem(16),
  borderRadius: pxToRem(16),
  border: `${pxToRem(1)} solid ${theme.color.whiteTrans20}`,
  background:
    'linear-gradient(129deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)',
  backdropFilter: `blur(${pxToRem(8)})`,
});

const locale = {
  en: {
    ...modelLocale.en,
    aiModels: 'AI models',
  },
};
